import { html, nothing } from 'lit';
import { ifDefined } from 'lit/directives/if-defined.js';
import { live } from 'lit/directives/live.js';
import { ref } from 'lit/directives/ref.js';
import { FloatField } from './float_field';

export default class FloatTextarea extends FloatField {
  static get properties() {
    return {
      ...super.properties,
      strippedValue: { type: String, state: false, reflect: false },
      singleLine: { type: Boolean, reflect: true, attribute: 'single-line' },
      liveWords: { type: String, state: false, reflect: false },
      maxWords: { type: Number, attribute: 'max-words' },
      noCounter: { type: Boolean, reflect: true, attribute: 'no-counter' },
      noInnerLabel: { type: Boolean, reflect: true, attribute: 'no-inner-label' },
    };
  }

  constructor() {
    super();
    this.type = 'textarea';
  }

  get value() {
    return this.input?.value || '';
  }

  set value(value) {
    const words = value
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
    this.liveWords = words > 0 ? words.toString() : '0';

    if (this.maxWords && this.hasInteracted) {
      this.announceWordCount(words, this.maxWords);
    }

    if (this.input) {
      this.input.value = value ?? '';
    }
    if (!this.hasInteracted) return;
    this._value = value ?? '';
    if (this.singleLine) {
      this.strippedValue = value.replace(/[\s\n]/g, ' ');
    }
    this.setFormValue(value || null);
    this.updateValidity();
    this.setInvalidState();
  }

  firstUpdated() {
    if (this.isConnected) {
      setTimeout(() => {
        if (this.input && this.input?.value) {
          this.hasInteracted = true;
          this.value = this.input.value;
        }
      }, 100);
    }
  }

  announceWordCount(wordCount, maxWords) {
    if (!maxWords || !window.announcements?.info) return;

    const percentage = (wordCount / maxWords) * 100;

    if (wordCount > maxWords) {
      window.announcements.warning(`Word limit exceeded: ${wordCount} of ${maxWords} words used`);
    } else if (percentage >= 90) {
      window.announcements.info(`Approaching word limit: ${wordCount} of ${maxWords} words used`);
    } else if (wordCount > 0 && wordCount % 10 === 0) {
      window.announcements.info(`${wordCount} of ${maxWords} words used`);
    }
  }

  handleInput(e) {
    super.handleInput(e);
    const words = this.value
      .trim()
      .split(/\s+/)
      .filter((word) => word.length > 0).length;
    this.liveWords = words > 0 ? words.toString() : '0';

    if (this.maxWords) {
      this.announceWordCount(words, this.maxWords);
    }

    if (this.maxWords && this.liveWords && this.liveWords > this.maxWords) {
      this.updateValidity();
      this.setInvalidState();
    }
  }

  renderLabel() {
    if (this.noInnerLabel === true) return nothing;
    return this.label ? html`<label for="${this.id}"> ${this.label} </label>` : nothing;
  }

  renderInput() {
    return html`
      ${this.singleLine
        ? html`<input
            type="hidden"
            id=${`${this.id}_hidden`}
            name=${`${ifDefined(this.name)}`}
            value=${live(this.strippedValue)}
          />`
        : nothing}

      <textarea
        ${ref((el) => (this.input = el))}
        id=${this.id}
        pattern=${ifDefined(this.pattern)}
        placeholder=${ifDefined(this.placeholder)}
        name=${ifDefined(this.singleLine ? undefined : this.name)}
        aria-label=${ifDefined(this.label)}
        aria-placeholder=${ifDefined(this.placeholder)}
        aria-describedby=${`${this.id}_${this.type}_error_message`}
        .value=${live(this.value)}
        ?readonly=${this.readOnly}
        ?disabled=${this.disabled}
        ?required=${this.required}
        ?autofocus=${this.autofocus}
        tabindex=${this.tabIndex}
        inputmode=${ifDefined(this.inputmode)}
        min=${ifDefined(this.validateOnly ? undefined : this.min)}
        max=${ifDefined(this.validateOnly ? undefined : this.max)}
        minlength=${ifDefined(this.minLength)}
        maxlength=${ifDefined(this.validateOnly ? undefined : this.maxLength)}
        step=${ifDefined(this.step)}
        aria-invalid=${this.invalid ? 'true' : 'false'}
        @change=${this.handleChange}
        @input=${this.handleInput}
        @focus=${this.handleFocus}
        @blur=${this.handleBlur}
      ></textarea>

      ${this.maxWords && !this.noCounter
        ? html`<p class="max_words_info">
            ${this.liveWords ? this.liveWords : '0'}/${this.maxWords}
          </p>`
        : nothing}
    `;
  }
}

window.customElements.get('float-textarea') ||
  window.customElements.define('float-textarea', FloatTextarea);
